"use client"

/**
 * One chosen category, the same everywhere a question's categories are shown:
 * the editor and the question sheet. Name first, then where it sits. Clicking
 * the card opens the selection; the × removes it.
 */

import * as React from "react"

import { CategoryPath } from "@/components/exam-categories/category-path"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tip } from "@/components/ui/tip"
import {
  describeProvenance,
  getCategoryBreadcrumb,
  type CategoryProvenanceMap,
  type ExamCategory,
} from "@/lib/exam-categories"

const WHEN_FORMAT = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
})

/** One chosen category: its whole path, its name, who set it, and a remove. */
export function ChosenCategory({
  category,
  categories,
  rootLabel,
  record,
  disabled,
  onOpen,
  onRemove,
  onVerify,
}: {
  category: ExamCategory
  categories: ExamCategory[]
  /** Where the path starts: the course, or the standard when there is none. */
  rootLabel: string
  record?: CategoryProvenanceMap[string] | undefined
  disabled: boolean
  /** Clicking the card opens the category selection. */
  onOpen?: () => void
  /** Absent means read-only: no remove. */
  onRemove?: () => void
  /** The director confirms a tag that is waiting for review. */
  onVerify?: () => void
}) {
  const trail = getCategoryBreadcrumb(categories, category.id)
  // A top-level category still sits somewhere: under its course.
  const path = [rootLabel, ...trail.slice(0, -1).map((node) => node.name)]
    .filter(Boolean)
    .join(" › ")
  const fullPath = [rootLabel, ...trail.map((node) => node.name)]
    .filter(Boolean)
    .join(" › ")

  // The full path on hover. The evidentiary record (who, how, when) is one
  // click away on the card's shield, where an auditor would look for it.
  const hover = fullPath
  const provenance = record ? describeProvenance(record) : null
  return (
    <Tip label={hover} side="left">
      <li className="group flex list-none items-start gap-1 rounded-md border border-border bg-background pe-1.5 hover:bg-muted/40">
        <button
          type="button"
          disabled={disabled || !onOpen}
          onClick={onOpen}
          aria-label={`Change category, currently ${fullPath}`}
          className="flex min-w-0 flex-1 items-start gap-3 rounded-md px-3 py-2.5 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
        >
          <span
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-[var(--icon-disc-brand-bg)] text-[var(--icon-disc-brand-fg)]"
            aria-hidden="true"
          >
            <i className="fa-light fa-tag text-base" />
          </span>
          <CategoryPath
            className="flex-1"
            trail={[rootLabel, ...trail.slice(0, -1).map((node) => node.name)]}
            name={category.name}
          />
        </button>
        {record && provenance ? (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className="mt-2.5 shrink-0 text-muted-foreground"
                aria-label={`How ${category.name} was tagged`}
              >
                <i className="fa-light fa-shield-check text-xs" aria-hidden="true" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 text-sm">
              <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
                <dt className="text-muted-foreground">Origin</dt>
                <dd className="m-0">{provenance.origin}</dd>
                <dt className="text-muted-foreground">Approved by</dt>
                <dd className="m-0">{record.by}</dd>
                <dt className="text-muted-foreground">When</dt>
                <dd className="m-0">{WHEN_FORMAT.format(new Date(record.at))}</dd>
                <dt className="text-muted-foreground">Status</dt>
                <dd className="m-0">{provenance.status}</dd>
              </dl>
              {onVerify && record.review === "pending" ? (
                <Button type="button" size="sm" className="mt-3" onClick={onVerify}>
                  Mark verified
                </Button>
              ) : null}
            </PopoverContent>
          </Popover>
        ) : null}
        {!disabled && onRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="mt-2.5 shrink-0 text-muted-foreground"
            aria-label={`Remove ${fullPath}`}
            onClick={onRemove}
          >
            <i className="fa-light fa-xmark text-xs" aria-hidden="true" />
          </Button>
        ) : null}
      </li>
    </Tip>
  )
}

